/* eslint-disable jira/react/no-style-attribute */
import React, { type ComponentType, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { useNativeScrollbarHeight } from '@atlassian/jira-portfolio-3-common/src/custom-scrollbar';
import { getContainerWidth, getContainerDuration } from '../../common/utils';
import { useZoomLevel, useContainer, useSmooth } from '../../controllers';
import { useKeyHandler, useResizeObserver } from './utils';
import ViewportVSync from './viewport-vsync';

export type HorizontalScrollViewComponentType = ComponentType<Props>;

type Props = {
	children: ReactNode;
};

const paddingToHideAutoHideScrollbar = 50;
const paddingSize = 1e6;

/**
 * Makes the container elements that resizing
 * @returns a DOM ref to the container el
 */
function useContainerRef() {
	const elRef = useRef<HTMLDivElement>(null);
	const [{ width }] = useContainer();

	useEffect(() => {
		const el = elRef.current;
		if (el) {
			el.style.width = `${width}px`;
		}
	}, [width]);

	return elRef;
}

export function HorizontalScrollableView({ children }: Props) {
	const scrollableRef = useRef<HTMLElement>(null);
	const containerRef = useContainerRef();
	const nativeScrollBarHeight = useNativeScrollbarHeight();
	const [zoomLevel] = useZoomLevel();
	const [smooth] = useSmooth();
	const [, { resize }] = useContainer();

	useKeyHandler(scrollableRef.current);

	const viewportRef = useResizeObserver(
		useCallback(
			(el: HTMLDivElement) => {
				if (!zoomLevel) {
					return;
				}
				const { width: viewportWidth, left, top } = el.getBoundingClientRect();

				resize({
					viewport: { width: viewportWidth, position: { left, top } },
					container: {
						width: getContainerWidth(zoomLevel),
						duration: getContainerDuration(zoomLevel),
					},
				});
			},
			[resize, zoomLevel],
		),
	);

	return (
		<Box ref={viewportRef} xcss={viewportStyles}>
			<Box
				tabIndex={0}
				ref={scrollableRef}
				xcss={scrollableStyles}
				style={{
					bottom: `${-((nativeScrollBarHeight ?? 0) + paddingToHideAutoHideScrollbar)}px`,
					scrollBehavior: smooth ? 'smooth' : 'auto',
					paddingBottom: `${paddingToHideAutoHideScrollbar}px`,
				}}
			>
				<Box
					xcss={containerStyles}
					ref={containerRef}
					testId="portfolio-3-horizontal-scrolling.ui.scroll-view.container"
				>
					{children}
				</Box>
				<Box xcss={farRightStyles}> </Box>
			</Box>
			<ViewportVSync scrollableRef={scrollableRef} paddingSize={paddingSize} />
		</Box>
	);
}

/**
 * Provides scrollable view if the zoom level is set,
 * The layout fits 100% of its container, so make sure that the wrapper is non-static positioned.
 */
export default function HorizontalScrollView(props: Props) {
	const [zoomLevel] = useZoomLevel();
	useContainer();

	if (zoomLevel === undefined) {
		return <>{props.children}</>;
	}

	return <HorizontalScrollableView {...props} />;
}

const viewportStyles = xcss({
	position: 'absolute',
	top: 'space.0',
	left: 'space.0',
	right: 'space.0',
	bottom: 'space.0',
	overflowY: 'hidden',
});

const scrollableStyles = xcss({
	position: 'absolute',
	top: 'space.0',
	left: 'space.0',
	right: 'space.0',
	overflowX: 'scroll',
	overflowY: 'hidden',
	outline: 'none',
});

const containerStyles = xcss({
	height: '100%',
	transform: `translateX(${paddingSize}px)`,
});

const farRightStyles = xcss({
	position: 'absolute',
	top: 'space.0',
	left: `${2e6}px`,
	pointerEvents: 'none',
	opacity: 0,
});
